import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { Sparkle, X, PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { industryByKey } from "../lib/constants";
import ActionCard from "./copilot/ActionCard";

export default function CopilotPanel({ open, onClose }) {
  const loc = useLocation();
  const projectFromPath = loc.pathname.match(/^\/projects\/([^/]+)/)?.[1] || "";
  const projectId = projectFromPath || new URLSearchParams(loc.search).get("project")
    || localStorage.getItem("fg_last_project") || "";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(null);
  const [applied, setApplied] = useState({});
  const [drawSaved, setDrawSaved] = useState({});
  const [project, setProject] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setProject(null);
    api.get("/copilot/history", { params: { project_id: projectId } })
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {});
    if (projectId) {
      api.get(`/projects/${projectId}`).then(({ data }) => setProject(data)).catch(() => {});
    }
  }, [open, projectId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const postAssistant = (content) =>
    setMessages((m) => [...m, { role: "assistant", content, ts: new Date().toISOString() }]);

  const doUndo = async () => {
    setBusy("undo");
    try {
      const { data } = await api.post(`/copilot/${projectId}/revert`);
      postAssistant(data.message);
      setApplied({});
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(null); }
  };

  const doApply = async (m, key, changes, label) => {
    if (!projectId) return toast.error("Open a project to apply design changes");
    setBusy(key);
    try {
      const { data } = await api.post(`/copilot/${projectId}/apply-design`,
        { changes, label, copilot_message_id: m.id || null });
      setApplied((s) => ({ ...s, [m.id || key]: data.version }));
      postAssistant(data.confirmation);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(null); }
  };

  const doSaveDrawing = async (m, key, action) => {
    if (!projectId) return toast.error("Open a project to save drawing changes");
    setBusy(key);
    try {
      const { data } = await api.post(`/copilot/${projectId}/queue-drawing`,
        { drawing_type: action.drawing_type, changes: action.changes, copilot_message_id: m.id || null });
      setDrawSaved((s) => ({ ...s, [m.id || key]: true }));
      toast.success(`Saved — ${data.queued_count} queued drawing action(s) on project`);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(null); }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, ts: new Date().toISOString() }]);
    if (msg.toLowerCase() === "undo") {
      if (!projectId) { postAssistant("Open a project first — undo reverts its active design version."); return; }
      await doUndo();
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/copilot/chat",
        { message: msg, project_id: projectId || null, context_screen: loc.pathname });
      setMessages((m) => [...m, {
        id: data.message_id, role: "assistant", content: data.reply, action: data.action,
        ts: new Date().toISOString(),
      }]);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSending(false);
    }
  };

  const industry = project ? industryByKey(project.industry) : null;

  return (
    <div
      data-testid="copilot-panel"
      className={`shrink-0 bg-[var(--c-surface)] border-l border-[var(--c-border)] flex flex-col transition-all duration-150 ease-out overflow-hidden ${
        open ? "w-96" : "w-0 border-l-0"
      }`}
    >
      <div className="h-14 shrink-0 flex items-center gap-2 px-4 border-b border-[var(--c-border)] min-w-96">
        <Sparkle size={16} weight="fill" className="text-[var(--c-accent)]" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[var(--c-text)]">AI Copilot</div>
          <div className="text-[10px] font-mono tracking-widest text-[var(--c-muted)] uppercase truncate" data-testid="copilot-context">
            {projectId && !project ? "Loading domain…" : industry ? `${industry.name} domain · live` : "General · live"}
          </div>
        </div>
        <button data-testid="copilot-close-btn" onClick={onClose} className="p-1 text-[var(--c-muted)] hover:text-[var(--c-text)]">
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-w-96" data-testid="copilot-messages">
        {messages.length === 0 && (
          <div className="text-xs text-[var(--c-muted)] leading-relaxed border border-dashed border-[var(--c-border)] rounded-sm p-3">
            Ask about specs, calculations, standards or next steps. Try “increase flux density to 1.7 T”,
            “what if 6.3 MVA?”, or “why did my hot spot fail?”.
            {industry ? ` Context: ${industry.name}.` : " Open a project to load its industry domain."}
          </div>
        )}
        {messages.map((m, i) => {
          const key = m.id || i;
          return (
            <div key={key} className={`fade-slide-in flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[92%] px-3 py-2 rounded-sm text-xs leading-relaxed ${
                  m.role === "user"
                    ? "bg-[var(--c-accent)] text-[var(--c-text)]"
                    : "bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)]"
                }`}
                data-testid={`copilot-msg-${m.role}-${i}`}
              >
                <div className="whitespace-pre-wrap">{m.content}</div>
                {m.role === "assistant" && m.action && (
                  <ActionCard action={m.action} msgKey={i}
                    appliedVersion={applied[m.id || i]} drawingSaved={drawSaved[m.id || i]}
                    busy={busy === i || busy === "undo"}
                    onApply={(changes, label) => doApply(m, i, changes, label)}
                    onUndo={doUndo}
                    onSaveDrawing={(action) => doSaveDrawing(m, i, action)} />
                )}
              </div>
            </div>
          );
        })}
        {sending && (
          <div className="text-[10px] font-mono text-[var(--c-muted)] tracking-widest flex items-center gap-2" data-testid="copilot-typing">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--c-accent)] animate-pulse" /> CLAUDE IS THINKING…
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div className="shrink-0 p-3 border-t border-[var(--c-border)] min-w-96">
        <div className="flex gap-2">
          <input
            data-testid="copilot-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask the copilot… ('undo' reverts last apply)"
            className="flex-1 bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-3 py-2 text-xs text-[var(--c-text)] placeholder:text-[var(--c-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--c-accent)] focus:ring-offset-2 focus:ring-offset-[var(--c-surface)]"
          />
          <button
            data-testid="copilot-send-btn"
            onClick={send}
            disabled={sending}
            className="px-3 bg-[var(--c-accent)] text-[var(--c-text)] rounded-sm hover:bg-[var(--c-accent)]/85 disabled:opacity-50 transition-colors duration-150"
          >
            <PaperPlaneRight size={14} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
}
